import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import styled from "styled-components";
import Button from "../Generic/Button";
import { errorNotification, successNotification } from "../Generic/Notifications";
import LandingConnector from "../Landing/Connector/LandingConnector";
import { useNavigate, useParams } from "react-router-dom";
import InventoryPlanService from "./Services/InventoryPlanService";
import InputBox from "../Generic/InputBox";
import Checkbox from "../Generic/Checkbox";
import { deepCopy, isEmpty } from "../util/util";
import { DEFAULT_QUANTITY_UNIT } from "../config/app.config";

const Page = styled.div`
	height: 100%;
	width: 100%;
	display: flex;
	flex-direction: column;
	padding: 2rem;
	gap: 2rem;
	overflow-x: hidden;
`;

const Header = styled.div`
	display: flex;
	font-size: 2rem;
	font-weight: 600;
`;

const Block = styled.div`
	width: max-content;
	height: auto;
	display: flex;
	gap: ${(props) => props.gap || "1rem"};
	align-items: center;
`;

const Div = styled.div`
	background: ${(props) => props.background};
	color: ${(props) => props.color};
	position: ${(props) => props.position};
	width: ${(props) => props.width};
	display: flex;
	flex-direction: ${(props) => props.flexDirection};
	justify-content: ${(props) => props.jContent};
	align-items: ${(props) => props.alignItem};
	padding: ${(props) => props.padding};
	margin: ${(props) => props.margin};
	text-align: ${(props) => props.txtAlign};
	font-size: ${(props) => props.fontSize};
	gap: ${(props) => props.gap};
	overflow-y: ${(props) => props.overflowY};
	overflow-x: ${(props) => props.overflowX};
	height: ${(props) => props.height};
	max-height: ${(props) => props.maxHeight};
	min-height: ${(props) => props.minHeight};
	max-width: ${(props) => props.maxWidth};
	white-space: ${(props) => props.whiteSpace};
	border: ${(props) => props.border};
	border-radius: ${(props) => props.borderRadius};
	flex-wrap: ${(props) => props.flexWrap};
	@media (max-width: 1000px) {
		justify-content: ${(props) => props.jContentBefore1000};
	}
`;

function generateTableColumns({ updateTableData }) {
	return [
		{
			name: "Symbol",
			cell: (data) => <img height={"80px"} src={data.moduleSymbol} alt="module-symbol" />,
		},
		{
			name: "Module",
			selector: (data) => data.moduleId,
			sortable: true,
		},
		{
			name: "System",
			selector: (data) => data.systemId,
		},
		{
			name: "Ingredient",
			selector: (data) => data.name || data.itemId || "-",
		},
		{
			name: "Filled amount",
			cell: (data) => {
				return (
					<>
						<InputBox
							type="number"
							defaultValue={data?.filledAmount}
							onChange={(e) => updateTableData(data.moduleId, data.systemId, "filledAmount", e.target.valueAsNumber)}
						/>
						<div style={{ fontSize:"16px", marginLeft:"0.5rem" }} >{DEFAULT_QUANTITY_UNIT}</div>
					</>
				);
			},
		},
		{
			name: "Co dispense",
			cell: (data) => {
				return (
					<Checkbox
						value={data?.coDispense}
						onClick={(e) => updateTableData(data.moduleId, data.systemId, "coDispense", e.target.checked)}
					/>
				);
			},
		},
		{
			name: "Agitation Required",
			cell: (data) => {
				return (
					<Checkbox
						value={data?.agitationRequired}
						onClick={(e) => updateTableData(data.moduleId, data.systemId, "agitationRequired", e.target.checked)}
					/>
				);
			},
		},
	];
}

const InventoryPlanDetails = (props) => {
	const { machineID } = props;

	const { id } = useParams();

	const [loading, setLoading] = useState(false);
	const [lookupData, setLookupData] = useState({});
	const [tableData, setTableData] = useState({});
	const columns = generateTableColumns({ updateTableData });

	const navigate = useNavigate();

	function prepareDataTablePayload(data) {
		let tableData = [];
		const inventoryData = data;
		for (const moduleId in inventoryData.modules) {
			const moduleData = inventoryData.modules[moduleId];
			for (const systemId in moduleData.systems) {
				const systemData = moduleData.systems[systemId];
				tableData.push({
					moduleSymbol: moduleData.moduleSymbol,
					moduleId: moduleId,
					systemId: systemId,
					itemId: systemData.itemId,
					name: systemData.name,
					moduleType: moduleData.moduleType,
					filledAmount: systemData.filledAmount,
					coDispense: systemData.coDispense,
					agitationRequired: systemData.agitationRequired,
					layer: moduleData.layer,
				});
			}
		}

		return tableData;
	}

	function updateTableData(moduleId, systemId, key, value) {
		let newTableData = deepCopy(tableData);
		let tableRecordIndex = newTableData.findIndex((record) => record.moduleId === moduleId && record.systemId === systemId);
		newTableData[tableRecordIndex][key] = value;
		setTableData(newTableData);
	}

	async function getPlanData(id) {
		const { data, error } = await InventoryPlanService.getInventoryPlan(id);
		if (data) {
			const { plan } = data;
			const dataTablePayload = prepareDataTablePayload(plan);
			setTableData(dataTablePayload);
			setLookupData(data);
		}
		if (error) {
			errorNotification(error);
		}
	}

	async function fetchInventoryDetails() {
		const payload = {
			restaurantId: machineID,
			brandId: "kocafe",
		};
		const { data, error } = await InventoryPlanService.getInventoryDetails(payload);
		if (data) {
			setLookupData(data);
		}
		if (error) {
			errorNotification(error);
		}
	}

	function generateCreatePlanPayload(tableData) {
		let modules = {},
			payload = {};

		for (const record of tableData) {
			const { moduleId, systemId, itemId, coDispense, agitationRequired, action, filledAmount, moduleSymbol, layer } = record;

			if (action === "noChange") continue;

			if (modules[moduleId]) {
				modules[moduleId]["systems"][systemId] = {
					itemId,
					coDispense: coDispense ?? false,
					agitationRequired: agitationRequired ?? false,
					filledAmount: filledAmount ?? 0,
					action,
				};
			} else {
				modules[moduleId + ""] = {
					moduleShape: moduleSymbol || "",
					layer,
					systems: {
						[systemId + ""]: {
							itemId,
							coDispense: coDispense ?? false,
							agitationRequired: agitationRequired ?? false,
							filledAmount: filledAmount ?? 0,
							action,
						},
					},
				};
			}
		}

		if (isEmpty(modules)) {
			errorNotification("Plan Data Can't be empty");
			return;
		}

		payload = {
			name: lookupData.name,
			plan: { modules },
			restaurantId: machineID,
		};

		return payload;
	}

	async function saveInventoryPlan() {
		const payload = generateCreatePlanPayload(tableData);
		if (!payload) return;
		const { data, error } = await InventoryPlanService.updateInventoryPlan(id, payload);
		if (data) {
			successNotification("Success");
			const { _id } = data;
			navigate(`/${machineID}/inventoryPlan/details/${_id}`);
		}
		if (error) {
			errorNotification(error);
		}
	}

	async function onPageLoad() {
		let promises = [];
		promises.push(getPlanData(id));
		promises.push(fetchInventoryDetails());

		Promise.all(promises)
			.then(() => setLoading(false))
			.catch((error) => {
				console.log(error);
				setLoading(false);
			});
	}

	useEffect(() => {
		onPageLoad();
	}, []);

	return (
		<Page>
			<Block>
				<span
					onClick={() => navigate(`/${machineID}/inventoryPlan/details/${id}`)}
					className="material-symbols-outlined"
					style={{ cursor: "pointer" }}>
					arrow_back
				</span>
				<Header>{lookupData.name || id}</Header>
			</Block>
			<DataTable
				allowOverflow
				progressPending={loading}
				style={{ height: "100%", width: "100%" }}
				striped
				fixedHeader
				columns={columns}
				data={tableData}
			/>
			<Button height="" onClick={() => saveInventoryPlan()}>
				Save Plan
			</Button>
		</Page>
	);
};

export default LandingConnector(InventoryPlanDetails);
